import { cva } from 'class-variance-authority';
import type { StyleProp, TextStyle } from 'react-native';
import type {
  TypographyColors,
  TypographyTextAlign,
  TypographyVariants,
  TypographyWeight,
} from './types';

export const typographyVariants = cva('', {
  variants: {
    variant: {
      'display-2xl': 'display-2xl',
      'display-xl': 'display-xl',
      'display-lg': 'display-lg',
      'display-md': 'display-md',
      'display-sm': 'display-sm',
      'display-xs': 'display-xs',
      'text-xl': 'text-xl',
      'text-lg': 'text-lg',
      'text-md': 'text-md',
      'text-sm': 'text-sm',
      'text-xsm': 'text-xsm',
      'text-xs': 'text-xs',
      'text-xxs': 'text-xxs',
      'text-xxxs': 'text-xxxs',
      'text-code-xs': 'text-code-xs',
      'text-code-xxs': 'text-code-xxs',
      'text-code-xxxs': 'text-code-xxxs',
      'para-xs': 'para-xs',
      'para-sm': 'para-sm',
      'para-xmd': 'para-xmd',
      'para-md': 'para-md',
    } satisfies Record<TypographyVariants, string>,
    align: {
      left: 'text-start',
      center: 'text-center',
      right: 'text-end',
      justify: 'text-justify',
    } satisfies Record<TypographyTextAlign, string>,
    weight: {
      thin: 'font-thin',
      'extra-light': 'font-extralight',
      light: 'font-light',
      regular: 'font-normal',
      medium: 'font-medium',
      'semi-bold': 'font-semibold',
      bold: 'font-bold',
      'extra-bold': 'font-extrabold',
      black: 'font-black',
    } satisfies Record<TypographyWeight, string>,
    color: {
      'text-brand-primary': 'text-brand-primary',
      'text-brand-secondary': 'text-brand-secondary',
      'text-brand-tertiary': 'text-brand-tertiary',
      'text-disabled': 'text-disabled',
      'text-error-primary': 'text-error-primary',
      'text-error-secondary': 'text-error-secondary',
      'text-placeholder': 'text-placeholder',
      'text-primary': 'text-primary',
      'text-quaternary': 'text-quaternary',
      'text-secondary': 'text-secondary',
      'text-success-primary': 'text-success-primary',
      'text-success-secondary': 'text-success-secondary',
      'text-tertiary': 'text-tertiary',
      'text-warning-primary': 'text-warning-primary',
      'text-warning-secondary': 'text-warning-secondary',
      'text-white': 'text-white',
    } satisfies Record<TypographyColors, string>,
  },
});

// sometimes in safari line-clamp doesn't work, so we need to use max-height
// max-height = line-height * number-of-lines
// ref: https://github.com/CezaryDanielNowak/React-dotdotdot/blob/master/src/clamp.js
// 1rem is a fallback if --typography-line-height isn't set
// --typography-line-height is set in packages/style/src/tailwind_extension.css

export function getTypographyLineHeight(variant?: string) {
  switch (variant) {
    case 'display-2xl':
      return 90;
    case 'display-xl':
      return 72;
    case 'display-lg':
      return 48;
    case 'display-md':
      return 44;
    case 'display-sm':
      return 38;
    case 'display-xs':
      return 32;
    case 'text-xl':
      return 30;
    case 'text-lg':
      return 28;
    case 'text-md':
      return 24;
    case 'text-sm':
      return 20;
    case 'text-xsm':
      return 18;
    case 'text-xs':
      return 16;
    case 'text-xxs':
      return 12;
    case 'text-xxxs':
      return 9;
    case 'text-code-xs':
      return 16;
    case 'text-code-xxs':
      return 12;
    case 'text-code-xxxs':
      return 9;
    default:
      return 16;
  }
}

export const truncateStyle = ({
  maxNumberOfLines,
  minNumberOfLines,
  variant,
}: {
  maxNumberOfLines?: number;
  minNumberOfLines?: number;
  variant?: string;
}): StyleProp<TextStyle> | undefined => {
  if (!minNumberOfLines && !maxNumberOfLines) {
    return undefined;
  }
  const typographyLineHeight = getTypographyLineHeight(variant);
  let styles = {};

  if (minNumberOfLines) {
    styles = {
      minHeight: typographyLineHeight * minNumberOfLines,
    };
  }

  return !maxNumberOfLines || maxNumberOfLines <= 0
    ? styles
    : {
        ...styles,
        overflow: 'hidden',
        maxHeight: typographyLineHeight * maxNumberOfLines,
      };
};
